import appSettings from 'shared/settings/appSettings';
import {
    RailDocument,
    DocumentType,
    NewDocument,
    DocumentHistory,
    Notifications,
    RailDocumentPendingActions,
    DocumentComment,
    DocumentSearchQuery,
    DocumentSearchResult,
    DocumentSearchReplaceSuggestion,
    DocumentDepartment
} from '../models/api/models';
import { signinRedirect, getToken } from 'overrides/api';
import { applicationState } from 'shared/store/applicationState';
import { httpDelete, httpPost, httpPut } from 'shared/services/httpService';
import { PagedResult } from '../models/api/models';

const documentUriPath = new URL('/api/documents', appSettings.api.portalApi);
const writableDocumentUriSuffixPath = '/writable';
const railDocumentUriSuffixPath = '/rail';
const documentViewedUriSuffixPath = '/view';
const documentAcknowledgedUriSuffixPath = '/acknowledge';
const documentClarifyUriSuffixPath = '/clarify';
const documentFeedbackUriSuffixPath = '/feedback';
const documentHistoryUriSuffixPath = '/history';

export const documentFilePath = new URL('/api/document-files', appSettings.api.portalApi);
const documentTypesPath = new URL('/api/document-types', appSettings.api.portalApi);
const writableDocumentTypesPath = new URL('/api/document-types/writable', appSettings.api.portalApi);
const rescindDocumentPath = new URL('/api/documents/rescind', appSettings.api.portalApi);
const deleteDocumentPath = new URL('/api/documents/delete', appSettings.api.portalApi);
const permanentlyDeleteDocumentPath = new URL('/api/documents/full-delete', appSettings.api.portalApi);
const restoreDocumentPath = new URL('/api/documents/restore', appSettings.api.portalApi);
const notificationsUriPath = new URL('/api/notifications', appSettings.api.portalApi);
const testUriPath = new URL('/api/test', appSettings.api.portalApi);
const validateDocumentPath = new URL('/api/documents/validate', appSettings.api.portalApi);

const myDocumentsUri = `${documentUriPath}/current-user/myDocuments`;
const actionableDocumentsUri = `${documentUriPath}/current-user/actionable`;
const allNotifiedDocumentsUri = `${documentUriPath}/current-user/allnotified`;
const rulebookDocumentUri = `${documentUriPath}/rulebook`;
const railDocumentUri = `${documentUriPath}/rail`;
const documentDepartmentsPath = new URL('/api/document-departments/departments', appSettings.api.portalApi);

// TODO if this is used in mobile app it will need to be overridden
const convertModelToFormData = (model: any, form: FormData | null = null, namespace = ''): FormData => {
    let formData = form || new FormData();

    for (let propertyName in model) {
        if (!model.hasOwnProperty(propertyName) || !model[propertyName]) continue;
        let formKey = namespace ? `${namespace}[${propertyName}]` : propertyName;
        if (model[propertyName] instanceof Date)
            formData.append(formKey, model[propertyName].toISOString());
        else if (model[propertyName] instanceof Array) {
            model[propertyName].forEach((element: any, index: number) => {
                formData.append(formKey, model[propertyName][index]);
            });
        }
        else if (typeof model[propertyName] === 'object' && !(model[propertyName] instanceof File))
            convertModelToFormData(model[propertyName], formData, formKey);
        else if (model[propertyName] instanceof File)
            formData.append(formKey, model[propertyName]);
        else
            formData.append(formKey, model[propertyName].toString());
    }

    return formData;
}

export interface DocumentLists {
    toAcknowledge: RailDocument[];
}

export interface MyDocuments {
    toAcknowledge: RailDocument[];
}



export const getActionableDocuments = async (mode: string) => {

    // when the mode is 'cbtc', we want to fetch both traditional and cbtc documents
    let apiMode = mode;
    if (mode === "cbtc") {
        apiMode = "both";
    }

    const result = await http<RailDocument[]>(`${actionableDocumentsUri.toString()}/${apiMode}`).catch((e) => {
        console.log(e);
    }) as RailDocument[];

    return result;
};

/* Documents Apis */
export const getMyDocuments = async (pageSize: number = -1, pageNumber: number = 1, mode: string) => {

    // when the mode is 'cbtc', we want to fetch both traditional and cbtc documents
    let apiMode = mode;
    if (mode === "cbtc") {
        apiMode = "both";
    }

    return await http<PagedResult<RailDocument>>(`${myDocumentsUri.toString()}/${apiMode}/${pageSize}/${pageNumber}`).catch((e) => {
        console.log(e);
    }) as PagedResult<RailDocument>;
};

export const getAllNotifiedDocuments = async (mode: string) => {

    // when the mode is 'cbtc', we want to fetch both traditional and cbtc documents
    let apiMode = mode;
    if (mode === "cbtc") {
        apiMode = "both";
    } 

    return await http<RailDocument[]>(`${allNotifiedDocumentsUri.toString()}/${apiMode}`).catch((e) => {
        console.log(e);
    }) as RailDocument[];
}

const handleError = (e: any, msg: string) => {
    console.log(msg);
    console.log(e);
};

export const getRulebookDocuments =
    async (mode: string) => await http<RailDocument[]>(`${rulebookDocumentUri.toString()}/${mode}`)
        .then(e => {
            return e;
        })
        .catch((e) => {
            console.log('error getting all documents')
            console.log(e);
        }) as RailDocument[];

export const getRailDocuments =
      async (mode: string) => await http<RailDocument[]>(`${railDocumentUri.toString()}/${mode}`)
        .then(e => {
            return e;
        })
        .catch((e) => {
            console.log('error getting all documents')
            console.log(e);
        }) as RailDocument[];

export const getAllWritableDocuments =
    async (mode: string) => await http<RailDocument[]>(`${documentUriPath.toString()}${writableDocumentUriSuffixPath}/${mode}`)
        .then(e => {
            return e;
        })
        .catch((e) => {
            console.log('error getting all documents')
            console.log(e);
        }) as RailDocument[];

export const getPdfBytes = async (docId: string) => {
    const pdfResponse = await http_response(`${documentFilePath}/${docId}`)
        .catch(e => { console.log(e); return {} as Response });
    return pdfResponse.arrayBuffer();
}

export async function getAllDocumentTypes(): Promise<DocumentType[]> {
    return await http<DocumentType[]>(documentTypesPath.toString()).catch(() => []);
}

export async function getAllDocumentDepartments(): Promise<DocumentDepartment[]> {
    return await http<DocumentDepartment[]>(documentDepartmentsPath.toString()).catch(() => []);
}

export async function getWritableDocumentTypes(): Promise<DocumentType[]> {
    return await http<DocumentType[]>(writableDocumentTypesPath.toString()).catch(() => []);
}

export const getRailDocumentTypes = async () => {
    const docTypes = await getAllDocumentTypes().catch((e) => { console.log(e); return [] });
    const filteredDocTypes = docTypes.filter((x: DocumentType) => x.documentModuleId !== 1);
    return filteredDocTypes;
};

export const getDocument = async (id: number, mode: string) => {
    return await http<RailDocument>(`${documentUriPath.toString()}/${mode}/${id}`)
        .catch(e => { console.log(e); return {} as RailDocument });
}

export const getDocumentHistory = async (id: number) => {
    return await http<DocumentHistory[]>(`${documentUriPath.toString()}/${id}${documentHistoryUriSuffixPath}`)
        .catch(e => { console.log(e); return {} as DocumentHistory[] });
}

export async function setDocumentViewed(id: number): Promise<RailDocumentPendingActions> {
    const header = await getAuthHeader();
    const response = await fetch(`${documentUriPath.toString()}/${id}${documentViewedUriSuffixPath}`, {
        method: 'PUT',
        headers: header,
    });
    const pendingActions = await response.json();
    return pendingActions;
}

export async function setDocumentAcknowledged(id: number): Promise<boolean> {
    const header = await getAuthHeader();
    const response = await fetch(`${documentUriPath.toString()}/${id}${documentAcknowledgedUriSuffixPath}`, {
        method: 'PUT',
        headers: header
    });
    const success = await response.text() === 'true';
    return success;
}

export async function setDocumentClarification(id: number, comment: DocumentComment): Promise<boolean> {
    const formData = new FormData();
    const header = await getAuthHeader();
     convertModelToFormData(comment,formData);
    const response = await fetch(`${documentUriPath.toString()}/${id}${documentClarifyUriSuffixPath}`, {
        method: 'PUT',
        body: formData,
        headers: header
    });
    const success = await response.text() === 'true';
    return success;
}

export async function setDocumentFeedback(id: number, comment: DocumentComment): Promise<boolean> {
    const header = await getAuthHeader();
    const formData = new FormData();
    convertModelToFormData(comment, formData);
    const response = await fetch(`${documentUriPath.toString()}/${id}${documentFeedbackUriSuffixPath}`, {
        method: 'PUT',
        body: formData,
        headers: header
    });
    const success = await response.text() === 'true';
    return success;
}

export interface IDocumentValidationRequest {
    id?: number,
    //TODO: refactor ViewType enum so we can use it as a paramter type
    viewType: number,
    pageNumber: number,
    document: NewDocument
}

export interface IDictonary<T> {
    [name: string]: T
}

export interface IDocumentValidationResponse {
    errors: IDictonary<string>
}

const mapDocumentValidationRequestForPost = (req: IDocumentValidationRequest) => {
    if (!req.document.file) {
        return req;
    }

    // make a copy of the the document
    req.document = { ...req.document };

    // remove the file property
    delete req.document.file;

    // return the request
    return req;
};

export const validateDocument = async (req: IDocumentValidationRequest) => {
    return httpPost<IDocumentValidationResponse>(validateDocumentPath.toString(), mapDocumentValidationRequestForPost(req));
}

export const addDocument = async (document: NewDocument) => {
    //https://stackoverflow.com/questions/35192841/how-do-i-post-with-multipart-form-data-using-fetch
    const formData = convertModelToFormData(document);

    const response = await fetch(documentUriPath.toString(), {
        method: 'POST',
        body: formData,
        headers: await getAuthHeader(),
    });
    return response;
}

export const editDocument = async (id: number, document: NewDocument) => {
    const formData = convertModelToFormData(document);

    const header = await getAuthHeader();
    const response = await fetch(`${documentUriPath.toString()}/${id}`, {
        method: 'PUT',
        body: formData,
        headers: header,

    });
    return response;
}

export const rescindDocument = async (id: number, document: NewDocument) => {
    const formData = new FormData();
    convertModelToFormData(document, formData);
    const uri = `${rescindDocumentPath.toString()}/${id}`;
    console.log('attempting to put document to ' + uri);
    const headers = await getAuthHeader();
    const response = await fetch(uri, {
        method: 'PUT',
        body: formData,
        headers: headers,
    });
    return response;
}

export const deleteDocument = async (id: number) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${deleteDocumentPath.toString()}/${id}`, {
        method: 'PATCH',
        headers: headers,
    });
}

export const permanentDeleteDocument = async (id: number) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${permanentlyDeleteDocumentPath.toString()}/${id}`, {
        method: 'DELETE',
        headers: headers,
    });
}

export const restoreDocument = async (id: number) => {
    const headers = await getAuthHeader();
    const response = await fetch(`${restoreDocumentPath.toString()}/${id}`, {
        method: 'PATCH',
        headers: headers,
    });
    return response;
}


/* Notifications Api */
export const getNotifications = async (mode: string) => {

    // when the mode is 'cbtc', we want to fetch both traditional and cbtc documents
    let apiMode = mode;
    if (mode === "cbtc") {
        apiMode = "both";
    } 

    return await http<Notifications>(`${notificationsUriPath.toString()}/${apiMode}`)
        .catch(e => { console.log(e); return {} as Notifications });
}

export const getMobileNotifications = async () => {
    return await http<Notifications>(`${notificationsUriPath.toString()}/mobile`)
        .catch(e => { console.log(e); return {} as Notifications });
}


/* Favourite Api */
export const updateFavouriteFlagAsync =
    async (documentId: number, selected: boolean) =>
        await http<number>(`${documentUriPath.toString()}/updateFavouriteFlag/${documentId}/${selected}`)
            .catch(e => handleError(e, "error setting favourite")) as number;

export const setFavouriteFlagsAsync = async (ids: number[]) => {
    return await httpPost<number[]>(`${documentUriPath.toString()}/setFavouriteFlags`, ids);
};

/* Search Api */
export async function documentSearch(query: DocumentSearchQuery): Promise<DocumentSearchResult[]> {
    const headers = await getAuthHeader();
    const formData = convertModelToFormData(query);

    const uri = `${documentUriPath.toString()}/search`;

    const response = await fetch(uri, {
        method: 'POST',
        body: formData,
        headers: headers,
    });

    const results = await response.json();
    return results;
}

export async function documentSearchReplaceSuggestions(): Promise<DocumentSearchReplaceSuggestion[]> {
    const headers = await getAuthHeader();
    const uri = `${documentUriPath.toString()}/searchReplaceSuggestions`;

    const response = await fetch(uri, {
        method: 'GET',
        headers: headers,
    });

    const results = await response.json();
    return results;
}

export async function addSearchReplaceSuggestion(suggestion: DocumentSearchReplaceSuggestion): Promise<boolean> {
    const uri = `${documentUriPath.toString()}/addSearchReplaceSuggestion`;
    const response = await httpPost<boolean>(uri, suggestion);
    return response;
}

export async function editSearchReplaceSuggestion(suggestion: DocumentSearchReplaceSuggestion): Promise<boolean> {
    const uri = `${documentUriPath.toString()}/editSearchReplaceSuggestion`;
    const response = await httpPut<boolean>(uri, suggestion);
    return response;
}

export async function deleteSearchReplaceSuggestion(suggestionIds: number[]): Promise<boolean> {
    const uri = `${documentUriPath.toString()}/deleteSearchReplaceSuggestions`;
    const response = await httpDelete<boolean>(uri, suggestionIds);
    return response;
}

/* Test Api */
export const sendTestNotification = async (model: any) => {
    const formData = convertModelToFormData(model);
    const headers = await getAuthHeader();
    const response = await fetch(`${testUriPath.toString()}/send`, {
        method: 'POST',
        body: formData,
        headers: headers,
    });
    return response;
}

export async function getAuthHeader() {
    const token = await getToken();

    return {
        Authorization: `Bearer ${token}`
    }
}

async function http<T>(
    request: string
): Promise<T> {
    console.log('http ' + request)
    const response = await http_response(request);
    const body = await response.json();
    return body;
}

async function http_response(
    request: string
): Promise<Response> {
    const headers = await getAuthHeader();
    console.log
    const response = await fetch(request, {
        headers: headers
    });

    if (response.status == 401) {
        applicationState.setUser(undefined);
        signinRedirect();
    }
    return response;
}
